import React from 'react'
import { UncontrolledTooltip } from 'reactstrap'
import { Alert as AntAlert } from 'antd'

/**
 * Creates a tooltip component
 * @param {string} text - Tooltip text
 * @param {string} target - Target element ID for the tooltip
 * @returns {ReactNode} Tooltip component
 */
export const createTooltip = (text, target) => (
  <UncontrolledTooltip placement="top" target={target}>
    {text}
  </UncontrolledTooltip>
)

/**
 * Creates a remove button with up/down arrow controls for appraiser list items
 * @param {Object} params - Button creation parameters
 * @param {Function} params.removeAppraiserHandler - Handler for removing appraiser
 * @param {Function} params.upArrowHandler - Handler for moving appraiser up
 * @param {Function} params.downArrowHandler - Handler for moving appraiser down
 * @param {string|number} params.id - Appraiser ID
 * @param {Object} params.item - Appraiser item object
 * @param {boolean} params.disableAppraiserSelection - Whether appraiser selection is disabled
 * @returns {ReactNode|string} Remove button component or empty string
 */
export const getRemoveButton = ({
  removeAppraiserHandler,
  upArrowHandler,
  downArrowHandler,
  id,
  item,
  disableAppraiserSelection,
}) => {
  return disableAppraiserSelection ? (
    ''
  ) : (
    <div className="d-flex align-items-center justify-content-around">
      <div className="d-inline-block" onClick={() => removeAppraiserHandler(id, item)}>
        <i className="fa fa-minus-square appraiser-remove-button" aria-hidden="true"></i>
      </div>
      <div className="d-inline-block overflow-hidden">
        <div onClick={() => upArrowHandler(id, item)} className="d-flex arrow-container overflow-hidden">
          <i className="fas fa-sort-up arrow-sort"></i>
        </div>
        <div
          onClick={() => downArrowHandler(id, item)}
          className="d-flex align-items-end arrow-container overflow-hidden"
        >
          <i className="fas fa-sort-down arrow-sort"></i>
        </div>
      </div>
    </div>
  )
}

/**
 * Gets the job allocation message string
 * @param {string} job_type - Job type ('automatically' or 'manually')
 * @returns {string} Job allocation message
 */
export const getJobAllocationMessage = (job_type) => {
  // We only return these 2 job types from the defaultAllocationMode endpoint
  let job_type_str = 'Manual'
  if (job_type === 'automatically') {
    job_type_str = 'Automatic'
  }

  return `Job Allocation Type: ${job_type_str}`
}

/**
 * Creates an appraiser override warning alert component
 * @param {Object} params - Warning parameters
 * @param {Array} params.selected_products - Array of selected products
 * @param {number} params.numProducts - Number of products
 * @returns {ReactNode} Warning alert component
 */
export const getAppraiserOverrideWarning = ({ selected_products, numProducts }) => {
  const message =
    'You have selected a product that has vendor level pricing. The order fee may vary depending on which vendor accepts the order.'
  const styling = selected_products[0] && numProducts > 0 ? '' : 'mt-3'

  return (
    <div className={styling}>
      <AntAlert message={message} banner />
    </div>
  )
}

/**
 * Creates a job allocation warning alert component
 * @param {Object} params - Warning parameters
 * @param {string} params.job_type - Job type
 * @param {Array} params.selected_products - Array of selected products
 * @returns {ReactNode} Warning alert component
 */
export const getJobAllocatingWarning = ({ job_type, selected_products }) => {
  const product = selected_products[0]
  const showAutomaticAllocationMessage =
    job_type === 'automatically' && product && product.use_for_default_follow_up_behavior
  let message = getJobAllocationMessage(job_type)
  if (showAutomaticAllocationMessage) {
    message = `${message} to ${product.follow_up_appraiser}`
  }
  const styling = 'mt-3'

  return (
    <div className={styling}>
      <AntAlert message={message} type="info" />
    </div>
  )
}
